import * as React from 'react';

/**
 * Máquina de gestos del lienzo.
 *
 * Es el punto donde más fácil se cuelan los errores: un arrastre que no pinta o
 * un pellizco que sí. Por eso vive como reductor puro —se prueba con secuencias
 * de eventos sintéticos, sin canvas ni DOM— y el hook solo la conecta a los
 * Pointer Events.
 *
 * La regla que lo sostiene todo: **un dedo pinta, dos dedos navegan**. Un gesto
 * que empezó con dos dedos no vuelve a pintar aunque se levante uno.
 */

/** Recorrido máximo, en px, para que levantar el dedo siga siendo un toque. */
export const TAP_MOVE_PX = 8;

/** Recorrido de dos dedos a partir del cual el gesto se decide. */
export const MULTI_DECIDE_PX = 10;

/**
 * Cuánto debe dominar la separación sobre el recorrido para leer un pellizco.
 *
 * No es simétrico a propósito. Los Pointer Events llegan de a uno, así que
 * entre el movimiento de un dedo y el del otro la separación oscila aunque la
 * usuaria los mueva en paralelo; sin este sesgo, ese ruido se lee como zoom y
 * el desplazamiento se vuelve imposible. Un pellizco real supera el sesgo de
 * sobra: separar los dedos cambia la distancia el doble de lo que mueve el
 * punto medio.
 */
export const PINCH_BIAS = 1.6;

export interface GesturePointer {
  readonly id: number;
  readonly x: number;
  readonly y: number;
  readonly time: number;
}

export type GestureEvent =
  | { readonly type: 'down'; readonly pointer: GesturePointer }
  | { readonly type: 'move'; readonly pointer: GesturePointer }
  | { readonly type: 'up'; readonly pointer: GesturePointer }
  | { readonly type: 'cancel'; readonly pointerId: number };

/** Tramo del recorrido del dedo, en coordenadas del lienzo. */
interface StrokeSegment {
  readonly fromX: number;
  readonly fromY: number;
  readonly toX: number;
  readonly toY: number;
}

export type GestureOutput =
  | { readonly type: 'none' }
  | { readonly type: 'tap'; readonly x: number; readonly y: number }
  | ({ readonly type: 'strokeStart' } & StrokeSegment)
  | ({ readonly type: 'strokePaint' } & StrokeSegment)
  | { readonly type: 'strokeEnd' }
  | {
      readonly type: 'transform';
      readonly scale: number;
      readonly centerX: number;
      readonly centerY: number;
      readonly dx: number;
      readonly dy: number;
    };

/**
 * `pending` es el único estado que todavía puede ir a cualquier parte: de ahí
 * salen el toque y el trazo. `multi` es terminal hasta que se levantan todos
 * los dedos, y por eso levantar uno de los dos no deja al otro pintando.
 */
type GesturePhase = 'idle' | 'pending' | 'stroke' | 'multi';

interface Point {
  readonly x: number;
  readonly y: number;
}

export interface GestureState {
  readonly pointers: readonly GesturePointer[];
  readonly phase: GesturePhase;
  /** Dónde empezó el gesto de un solo dedo. */
  readonly origin: GesturePointer | null;
  /** Último punto del trazo, para saber qué tramo pintar. */
  readonly lastPoint: Point | null;
  /** Distancia entre los dos dedos en el cuadro anterior. */
  readonly pinchDistance: number | null;
  /** Punto medio entre los dos dedos en el cuadro anterior. */
  readonly pinchCenter: Point | null;
  /** Qué resultó ser el gesto de dos dedos. Se decide una vez y no cambia. */
  readonly multiMode: MultiMode;
  /** Separación y punto medio al apoyar el segundo dedo, para decidir. */
  readonly multiStartDistance: number | null;
  readonly multiStartCenter: Point | null;
}

/**
 * Un gesto de dos dedos desplaza **o** hace zoom, nunca ambos a la vez.
 * Mezclarlos es lo que hacía que el lienzo se escapara al intentar scroll.
 */
type MultiMode = 'undecided' | 'pan' | 'pinch';

const NONE: GestureOutput = { type: 'none' };

export function createGestureState(): GestureState {
  return {
    pointers: [],
    phase: 'idle',
    origin: null,
    lastPoint: null,
    pinchDistance: null,
    pinchCenter: null,
    multiMode: 'undecided',
    multiStartDistance: null,
    multiStartCenter: null,
  };
}

function distance(a: Point, b: Point): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

function midpoint(a: Point, b: Point): Point {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

function replacePointer(
  pointers: readonly GesturePointer[],
  pointer: GesturePointer,
): GesturePointer[] {
  return pointers.map((current) => (current.id === pointer.id ? pointer : current));
}

interface GestureResult {
  readonly state: GestureState;
  readonly output: GestureOutput;
}

const STROKE_END: GestureOutput = { type: 'strokeEnd' };

export function gestureReducer(state: GestureState, event: GestureEvent): GestureResult {
  switch (event.type) {
    case 'down': {
      const pointers = [...state.pointers, event.pointer];

      if (pointers.length === 1) {
        return {
          state: {
            ...state,
            pointers,
            phase: 'pending',
            origin: event.pointer,
            lastPoint: event.pointer,
            pinchDistance: null,
            pinchCenter: null,
          },
          output: NONE,
        };
      }

      // El segundo dedo convierte el gesto en navegación. Si había un trazo en
      // curso se cierra aquí: lo pintado se queda, pero no se pinta más.
      const [first, second] = pointers as [GesturePointer, GesturePointer];
      const startDistance = distance(first, second);
      const startCenter = midpoint(first, second);
      const multi: GestureState = {
        ...state,
        pointers,
        phase: 'multi',
        origin: null,
        lastPoint: null,
        pinchDistance: startDistance,
        pinchCenter: startCenter,
        multiMode: 'undecided',
        multiStartDistance: startDistance,
        multiStartCenter: startCenter,
      };

      return { state: multi, output: state.phase === 'stroke' ? STROKE_END : NONE };
    }

    case 'move': {
      const previous = state.pointers.find((current) => current.id === event.pointer.id);
      if (!previous) return { state, output: NONE };

      const pointers = replacePointer(state.pointers, event.pointer);

      if (pointers.length >= 2) {
        const [first, second] = pointers as [GesturePointer, GesturePointer];
        const current = distance(first, second);
        const center = midpoint(first, second);

        // Se compara contra el inicio del gesto y no contra el cuadro
        // anterior: así el ruido de los eventos intercalados no decide nada.
        const spread = Math.abs(current - (state.multiStartDistance ?? current));
        const travel = state.multiStartCenter ? distance(center, state.multiStartCenter) : 0;

        const multiMode: MultiMode =
          state.multiMode !== 'undecided'
            ? state.multiMode
            : Math.max(spread, travel) <= MULTI_DECIDE_PX
              ? 'undecided'
              : spread > travel * PINCH_BIAS
                ? 'pinch'
                : 'pan';

        const settled: GestureState = {
          ...state,
          pointers,
          phase: 'multi',
          pinchDistance: current,
          pinchCenter: center,
          multiMode,
        };

        // Mientras no se sepa qué gesto es, no se mueve nada: son los primeros
        // milímetros, y equivocarse ahí es lo que arruina el desplazamiento.
        if (multiMode === 'undecided') return { state: settled, output: NONE };

        if (multiMode === 'pan') {
          return {
            state: settled,
            output: {
              type: 'transform',
              scale: 1,
              centerX: center.x,
              centerY: center.y,
              dx: state.pinchCenter ? center.x - state.pinchCenter.x : 0,
              dy: state.pinchCenter ? center.y - state.pinchCenter.y : 0,
            },
          };
        }

        return {
          state: settled,
          output: {
            type: 'transform',
            scale:
              state.pinchDistance && state.pinchDistance > 0 ? current / state.pinchDistance : 1,
            centerX: center.x,
            centerY: center.y,
            dx: 0,
            dy: 0,
          },
        };
      }

      if (state.phase === 'stroke') {
        const from = state.lastPoint ?? event.pointer;
        return {
          state: { ...state, pointers, lastPoint: event.pointer },
          output: {
            type: 'strokePaint',
            fromX: from.x,
            fromY: from.y,
            toX: event.pointer.x,
            toY: event.pointer.y,
          },
        };
      }

      if (state.phase !== 'pending' || !state.origin) {
        return { state: { ...state, pointers }, output: NONE };
      }

      // Por debajo del umbral el dedo todavía puede estar quieto: el trazo no
      // empieza, y por eso un pellizco no deja una mostacilla suelta.
      if (distance(state.origin, event.pointer) <= TAP_MOVE_PX) {
        return { state: { ...state, pointers }, output: NONE };
      }

      return {
        state: { ...state, pointers, phase: 'stroke', lastPoint: event.pointer },
        output: {
          type: 'strokeStart',
          fromX: state.origin.x,
          fromY: state.origin.y,
          toX: event.pointer.x,
          toY: event.pointer.y,
        },
      };
    }

    case 'up': {
      const pointers = state.pointers.filter((current) => current.id !== event.pointer.id);

      // Mientras quede algún dedo, el gesto sigue siendo de navegación.
      if (pointers.length > 0) {
        return {
          state: { ...state, pointers, phase: 'multi', pinchDistance: null, pinchCenter: null },
          output: NONE,
        };
      }

      const settled = createGestureState();

      if (state.phase === 'stroke') return { state: settled, output: STROKE_END };

      if (state.phase === 'pending') {
        return {
          state: settled,
          output: { type: 'tap', x: event.pointer.x, y: event.pointer.y },
        };
      }

      return { state: settled, output: NONE };
    }

    case 'cancel': {
      const pointers = state.pointers.filter((current) => current.id !== event.pointerId);
      const output = state.phase === 'stroke' ? STROKE_END : NONE;

      if (pointers.length === 0) return { state: createGestureState(), output };

      return {
        state: { ...state, pointers, phase: 'multi', lastPoint: null, origin: null },
        output,
      };
    }
  }
}

export interface CanvasGestureHandlers {
  readonly onPointerDown: (event: React.PointerEvent<HTMLCanvasElement>) => void;
  readonly onPointerMove: (event: React.PointerEvent<HTMLCanvasElement>) => void;
  readonly onPointerUp: (event: React.PointerEvent<HTMLCanvasElement>) => void;
  readonly onPointerCancel: (event: React.PointerEvent<HTMLCanvasElement>) => void;
}

/**
 * Conecta la máquina a los Pointer Events de un `<canvas>`.
 *
 * El estado vive en una ref y no en `useState`: un gesto produce decenas de
 * eventos por segundo y ninguno de ellos necesita re-renderizar el componente.
 */
export function useCanvasGestures(
  onGesture: (output: GestureOutput) => void,
): CanvasGestureHandlers {
  const stateRef = React.useRef<GestureState>(createGestureState());
  const callbackRef = React.useRef(onGesture);

  // La ref se actualiza tras el render, no durante: así `dispatch` puede
  // quedarse estable sin capturar una versión vieja del callback.
  React.useEffect(() => {
    callbackRef.current = onGesture;
  }, [onGesture]);

  const dispatch = React.useCallback((event: GestureEvent) => {
    const { state, output } = gestureReducer(stateRef.current, event);
    stateRef.current = state;
    if (output.type !== 'none') callbackRef.current(output);
  }, []);

  const toPointer = (event: React.PointerEvent<HTMLCanvasElement>): GesturePointer => {
    const bounds = event.currentTarget.getBoundingClientRect();
    return {
      id: event.pointerId,
      x: event.clientX - bounds.left,
      y: event.clientY - bounds.top,
      time: event.timeStamp,
    };
  };

  return {
    onPointerDown: (event) => {
      event.currentTarget.setPointerCapture(event.pointerId);
      dispatch({ type: 'down', pointer: toPointer(event) });
    },
    onPointerMove: (event) => {
      dispatch({ type: 'move', pointer: toPointer(event) });
    },
    onPointerUp: (event) => {
      dispatch({ type: 'up', pointer: toPointer(event) });
    },
    onPointerCancel: (event) => {
      dispatch({ type: 'cancel', pointerId: event.pointerId });
    },
  };
}
